import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const items = [
  {
    id: "conciliation",
    tag: "Amiable · Confidentiel",
    title: "Conciliation",
    body:
      "Vous choisissez la conciliation. Nous préparons la requête, réunissons les pièces financières et comptables, puis organisons la saisine du président du tribunal compétent.",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
  {
    id: "mandat",
    tag: "Amiable · Négocié",
    title: "Mandat ad hoc",
    body:
      "Vous choisissez le mandat ad hoc. Nous préparons la demande, constituons le dossier de pièces et organisons le dépôt auprès du tribunal compétent.",
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
  {
    id: "negociation",
    tag: "Créanciers · Trésorerie",
    title: "Négociation avec vos créanciers",
    body:
      "Vous nous indiquez les créanciers concernés et le résultat recherché. Nous préparons les documents, les éléments chiffrés et le calendrier des échanges.",
    image:
      "https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    id: "redressement",
    tag: "Collective · Continuité",
    title: "Redressement judiciaire",
    body:
      "Vous choisissez le redressement judiciaire. Nous rédigeons les actes, réunissons les pièces, organisons la prise de date et le dépôt auprès du greffe compétent.",
    image:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
  {
    id: "sauvegarde",
    tag: "Collective · Préventive",
    title: "Sauvegarde",
    body:
      "Vous choisissez la sauvegarde. Nous préparons la requête, le dossier économique et les pièces justificatives, puis suivons le dépôt auprès du greffe compétent.",
    image:
      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
  {
    id: "liquidation",
    tag: "Collective · Dépôt",
    title: "Liquidation judiciaire & dépôt de bilan",
    body:
      "Vous choisissez de déposer le bilan. Nous préparons la déclaration de cessation de paiement, réunissons les pièces et organisons la prise de date puis le dépôt auprès du greffe compétent.",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
];

const EXPERTISE_LINKS = {
  mandat: "/mandat-ad-hoc",
  conciliation: "/conciliation-entreprise",
  redressement: "/redressement-judiciaire",
  liquidation: "/liquidation-judiciaire-paris",
};

export default function Expertises() {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="solutions"
      data-testid="solutions-section"
      className="bg-[#efece5] py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="mb-14 grid grid-cols-12 md:mb-20"
        >
          <div className="col-span-12 md:col-span-3">
            <span data-testid="solutions-kicker" className="section-badge">§ Nos solutions</span>
          </div>
          <div className="col-span-12 mt-4 md:col-span-8 md:col-start-4 md:mt-0">
            <h2 className="font-serif-ed text-5xl leading-[1] tracking-tight text-[#1a1918] md:text-6xl lg:text-7xl">
              Vous choisissez la procédure.{" "}
              <span className="italic text-[#ce1e20]">Nous préparons le dossier</span>.
            </h2>
            <p className="mt-8 max-w-3xl text-base text-[#1a1918]/75 md:text-lg">
              Conciliation, mandat ad hoc, redressement, sauvegarde ou dépôt de
              bilan : vous retenez la procédure qui vous convient. ProSecours
              rédige les actes, réunit les pièces, demande la date auprès du
              tribunal lorsque nécessaire et organise le dépôt au greffe
              compétent.
            </p>
            <div
              data-testid="solutions-value-highlights"
              className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3"
            >
              <div className="bg-[#f7f5f0] p-4 border-l-2 border-[#ce1e20]">
                <div className="overline text-[#ce1e20]">Coût maîtrisé</div>
                <p className="mt-2 text-sm leading-relaxed text-[#1a1918]/75">
                  Une prise en charge claire pour les entreprises et
                  entrepreneurs en difficulté, sans frais inutiles.
                </p>
              </div>
              <div className="bg-[#f7f5f0] p-4 border-l-2 border-[#ce1e20]">
                <div className="overline text-[#ce1e20]">Dépôt dans les délais</div>
                <p className="mt-2 text-sm leading-relaxed text-[#1a1918]/75">
                  Les actes sont préparés rapidement et déposés à temps auprès
                  du greffe compétent.
                </p>
              </div>
              <div className="bg-[#f7f5f0] p-4 border-l-2 border-[#ce1e20]">
                <div className="overline text-[#ce1e20]">Équipe réactive</div>
                <p className="mt-2 text-sm leading-relaxed text-[#1a1918]/75">
                  Réponse sous 48 h ouvrées et traitement prioritaire des
                  échéances proches.
                </p>
              </div>
            </div>
            <a
              href="#audit"
              data-testid="solutions-demarche-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#ce1e20] px-6 py-3 text-white overline transition-colors hover:bg-[#a71518]"
            >
              Décrire ma démarche
              <span>→</span>
            </a>
          </div>
        </motion.div>

        <div className="border-t border-[#1a1918]/25">
          {items.map((it) => {
            const isOpen = open === it.id;
            return (
              <div
                key={it.id}
                className="border-b border-[#1a1918]/25"
                data-testid={`solution-item-${it.id}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : it.id)}
                  data-testid={`solution-trigger-${it.id}`}
                  className="group grid w-full grid-cols-12 items-center gap-6 py-8 text-left md:py-10"
                >
                  <span className="col-span-9 font-serif-ed text-3xl tracking-tight text-[#1a1918] md:col-span-7 md:text-5xl lg:text-6xl">
                    {it.title}
                  </span>
                  <span className="hidden md:col-span-4 md:block overline text-[#1a1918]/60">
                    {it.tag}
                  </span>
                  <span className="col-span-3 justify-self-end md:col-span-1">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#1a1918]/30 text-[#1a1918] transition-colors duration-300 group-hover:bg-[#1a1918] group-hover:text-[#f7f5f0]">
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease: [0.77, 0, 0.175, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-12 gap-6 pb-10 md:gap-8 md:pb-14">
                        <div className="col-span-12 md:col-span-5 md:col-start-2">
                          <p className="text-base leading-relaxed text-[#1a1918]/80 md:text-lg">
                            {it.body}
                          </p>
                          <a
                            href="#audit"
                            className="ed-link mt-6 inline-block font-serif-ed text-2xl italic text-[#1a1918]"
                          >
                            Décrire ma démarche →
                          </a>
                          {EXPERTISE_LINKS[it.id] && (
                            <a
                              href={EXPERTISE_LINKS[it.id]}
                              data-testid={`solution-link-${it.id}`}
                              className="mt-4 block overline text-[#ce1e20] transition-colors hover:text-[#a71518]"
                            >
                              Comprendre : {it.title.toLowerCase()} →
                            </a>
                          )}
                        </div>
                        <div className="col-span-12 md:col-span-5 md:col-start-8">
                          <div className="aspect-[4/3] overflow-hidden">
                            <img
                              src={it.image}
                              alt={it.title}
                              className="h-full w-full object-cover"
                              style={{ filter: "grayscale(30%) contrast(1.05)" }}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
